import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { PostFeeLevelsDTO } from '../../../../api';

type CreateFeeLevelModalProps = {
  open: boolean;
  onClose: () => void;
  onCreate: () => void;
  fields: PostFeeLevelsDTO | null;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  isLoading: boolean;
};

export const CreateFeeLevelModal: FC<CreateFeeLevelModalProps> = (props) => {
  const { open, onClose, onCreate, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // validation for required fields
  const requiredFields = ['min_turnover'] as (keyof PostFeeLevelsDTO)[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined) {
        return true;
      }
    }

    if (Number(fields.min_turnover) < 0) return true;

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('fee-levels.modals.create.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="min_turnover"
            type="number"
            value={fields?.min_turnover ?? ''}
            onChange={(e) => fieldsHandler(e)}
            inputProps={{ min: 0, step: '0.01' }}
            label={t('fee-levels.modals.create.fields.min-turnover')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onCreate()}
          disabled={isLoading || checkSubmitDisabling()}
        >
          {t('fee-levels.modals.create.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()}>
          {t('fee-levels.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
